import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { animate, stagger, scrambleText, createLayout } from 'animejs';
import './Services.css';
import AnimatedBackground from './AnimatedBackground';

const Services = () => {
  const { t } = useTranslation();
  const gridRef = useRef(null);
  const leftRef = useRef(null);
  const headlineRef = useRef(null);
  const subtitleRef = useRef(null);
  const layoutRef = useRef(null);
  const revealedRef = useRef(false);

  const navigateToPage = (path) => {
    window.history.pushState({}, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const services = [
    {
      titleKey: 'services.store.title',
      icon: 'project-diagram',
      descKey: 'services.store.description',
      buttonKey: 'services.store.button',
      path: '/proyectos',
      accent: 'warm',
    },
    {
      titleKey: 'services.community.title',
      icon: 'users',
      descKey: 'services.community.description',
      buttonKey: 'services.community.button',
      path: '/comunidad',
      accent: 'nature',
    },
  ];

  useEffect(() => {
    const grid = gridRef.current;
    const left = leftRef.current;
    if (!grid || !left) return undefined;

    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const headline = headlineRef.current;
    const subtitle = subtitleRef.current;
    const headlineText = t('services.headline');

    const runTextFX = () => {
      if (reduced) return;
      if (headline) {
        headline.innerHTML = '';
        animate(headline, {
          innerHTML: scrambleText({
            text: headlineText,
            reversed: true,
            duration: 1400,
            revealRate: 45,
          }),
          duration: 1400,
          ease: 'linear',
        });
      }
      if (subtitle) {
        subtitle.style.opacity = '0';
        animate(subtitle, {
          opacity: [0, 1],
          translateY: [18, 0],
          delay: 500,
          duration: 700,
          ease: 'outExpo',
        });
      }
    };

    const runGridFX = () => {
      if (revealedRef.current) return;
      revealedRef.current = true;

      const cards = grid.querySelectorAll('.service-card-ecosystem');
      if (reduced) {
        cards.forEach((c) => {
          c.style.opacity = '1';
          c.style.transform = '';
        });
        return;
      }

      try {
        const layout = createLayout(grid, { children: '.service-card-ecosystem' });
        layoutRef.current = layout;
        cards.forEach((card) => {
          card.style.opacity = '0';
          card.style.transform = 'scale(0.88)';
        });
        layout.record();
        cards.forEach((card) => {
          card.style.opacity = '1';
          card.style.transform = '';
        });
        layout.animate({
          duration: 750,
          ease: 'outExpo',
          delay: stagger(70),
        });
      } catch {
        animate(cards, {
          opacity: [0, 1],
          translateY: [40, 0],
          scale: [0.94, 1],
          delay: stagger(90),
          duration: 650,
          ease: 'outExpo',
        });
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          if (left) {
            left.style.opacity = reduced ? '1' : '0';
            if (!reduced) {
              animate(left, {
                opacity: [0, 1],
                translateX: [-32, 0],
                duration: 700,
                ease: 'outExpo',
              });
            }
          }
          runTextFX();
          runGridFX();
          observer.disconnect();
        });
      },
      { threshold: 0.08 }
    );

    observer.observe(grid);
    return () => {
      observer.disconnect();
      layoutRef.current?.revert?.();
    };
  }, [t]);

  return (
    <section className="services services-hero-inverted" id="ecosistema">
      <AnimatedBackground particleCount={6} staticCount={2} />
      <div className="services-inverted-layout">
        <div className="services-content-side services-content-side--animated" ref={leftRef}>
          <span className="services-tag">{t('services.tag')}</span>
          <h2 className="services-headline services-headline--xl" ref={headlineRef}>
            {t('services.headline')}
          </h2>
          <p className="services-subtitle services-subtitle--xl" ref={subtitleRef}>
            {t('services.subtitle')}
          </p>
        </div>

        <div className="services-grid-side services-grid-side--two" ref={gridRef}>
          {services.map((s) => (
            <div
              key={s.path}
              className={`service-card-ecosystem service-card-accent-${s.accent}`}
              onClick={() => navigateToPage(s.path)}
            >
              <div className="service-card-ecosystem-inner">
                <div className="service-icon-ecosystem">
                  <i className={`fas fa-${s.icon}`} />
                </div>
                <h3 className="service-title-ecosystem">{t(s.titleKey)}</h3>
                <p className="service-desc-ecosystem">{t(s.descKey)}</p>
                <button
                  type="button"
                  className="service-btn-ecosystem"
                  onClick={(e) => { e.stopPropagation(); navigateToPage(s.path); }}
                >
                  {t(s.buttonKey)}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
